/**
 * @author EdenCha <eden@nurigo.net>
 */
import React, { useState } from 'react'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'
import { NavLink } from 'react-router-dom'
import { setPropTypes, setDefaultProps } from 'lib/setPropTypes'
import withStyles from '@material-ui/core/styles/withStyles'
import classNames from 'classnames'

import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Drawer from '@material-ui/core/Drawer'
import Toolbar from '@material-ui/core/Toolbar'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import Fade from '@material-ui/core/Fade'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'

import Icon from '@material-ui/core/Icon'
import { RxOpenInNewWindow as RxOpenInNewWindowIcon } from 'react-icons/rx'
import {
  FaRegStar as FaRegStarIcon,
  FaStar as FaStarIcon
} from 'react-icons/fa'

import { isArray } from 'lib/detectType'
import isEqualProps from 'lib/isEqualProps'

const drawerWidth = 240
const styles = theme => ({
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: drawerWidth,
      flexShrink: 0
    }
  },
  drawerPaper: {
    width: drawerWidth,
    overflowX: 'hidden',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: 5
    }
  },
  mainGrid: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  subTitle: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  menuTitle: {
    paddingLeft: theme.spacing(2)
  },
  icon: {
    verticalAlign: 'bottom',
    fontSize: '1.1rem',
    marginRight: theme.spacing(1)
  },
  newTabIcon: {
    fontSize: '1em',
    verticalAlign: 'middle',
    marginLeft: 4
  },
  menuLink: {
    display: 'block',
    color: theme.palette.text.secondary,
    fill: theme.palette.text.secondary,
    textDecoration: 'none'
  },
  activeMenuLink: {
    fontWeight: 'bolder',
    color: theme.palette.primary.main,
    fill: theme.palette.primary.main,
    backgroundColor: '#f3f3f3'
  },
  menuItemBox: {
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  favoriteIconButton: {
    marginRight: theme.spacing(1)
  },
  favoriteIcon: {
    fontSize: '0.9em',
    color: '#ffaf00'
  },
  favoriteRegIcon: {
    color: '#c5c5c5'
  },
  betaText: {
    color: '#fff',
    fontSize: '0.7em',
    marginLeft: 4,
    // padding: 2,
    paddingLeft: 4,
    paddingRight: 4,
    fontWeight: 'bolder',
    borderRadius: 5,
    // verticalAlign: 'top',
    backgroundColor: '#333'
  }
})

export const useProps = {}

const MenuLink = props => {
  const { classes, newTab = false, path, exact, children } = props
  return newTab === true ? (
    <a href={path} target="_black" className={classes.menuLink} {...props}>
      {children}
    </a>
  ) : (
    <NavLink
      to={path}
      className={classes.menuLink}
      activeClassName={classes.activeMenuLink}
      exact={exact}
    >
      {children}
    </NavLink>
  )
}

const MenuItem = props => {
  const {
    classes,
    data: subMenuData = {},
    isFavorite = false,
    onClickFavoriteIcon
  } = props
  const [isHover, setHover] = useState(false)
  return (
    <Box
      key={subMenuData?.link}
      className={classes.menuItemBox}
      onMouseEnter={() => {
        setHover(true)
      }}
      onMouseLeave={() => {
        setHover(false)
      }}
    >
      <MenuLink
        classes={classes}
        newTab={subMenuData.newTab}
        path={subMenuData.link}
        exact={true}
      >
        <Grid container wrap="nowrap" spacing={1} alignItems="center">
          <Grid item xs>
            <Typography variant="body2" className={classes.menuTitle}>
              <Icon className={classes.icon}>{subMenuData.icon}</Icon>
              {subMenuData.name}
              {subMenuData?.isBeta === true && (
                <Typography variant="caption" className={classes.betaText}>
                  BETA
                </Typography>
              )}
              {subMenuData.newTab === true && (
                <RxOpenInNewWindowIcon className={classes.newTabIcon} />
              )}
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Fade in={isHover === true || isFavorite === true}>
              <Tooltip title={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 등록'}>
                <IconButton
                  size="small"
                  className={classes.favoriteIconButton}
                  onClick={event =>
                    onClickFavoriteIcon(event, isFavorite, {
                      name: subMenuData?.name,
                      link: subMenuData?.link
                    })
                  }
                >
                  {!isFavorite ? (
                    <FaRegStarIcon
                      className={classNames(classes.favoriteIcon, {
                        [classes.favoriteRegIcon]: !isFavorite
                      })}
                    />
                  ) : (
                    <FaStarIcon
                      className={classNames(classes.favoriteIcon, {
                        [classes.favoriteRegIcon]: !isFavorite
                      })}
                    />
                  )}
                </IconButton>
              </Tooltip>
            </Fade>
          </Grid>
        </Grid>
      </MenuLink>
    </Box>
  )
}

const MySiteLayoutDrawerMenu = React.memo(
  ({ classes, proxy, ...props }) => {
    const {
      open,
      width,
      menus = [],
      visibleHeader,
      favoriteMenu = [],
      setDrawerOpen,
      onClickFavoriteIcon
    } = props
    if (open !== true) return null
    return (
      <Drawer
        className={classes.drawer}
        classes={{ paper: classes.drawerPaper }}
        variant={isWidthUp('md', width) ? 'permanent' : 'temporary'}
        anchor="left"
        open={open}
        onClose={() => setDrawerOpen(false)}
      >
        {visibleHeader && <Toolbar />}
        {isArray(favoriteMenu) &&
          favoriteMenu.length > 0 &&
          favoriteMenu.filter(link => {
            const menuList = [].concat(
              ...menus.map(data => [...data.list.map(list => list?.link)])
            )
            return menuList.indexOf(link) !== -1
          }).length > 0 && (
            <>
              <Box mt={2} mb={1}>
                <Grid item xs={12}>
                  <Typography
                    variant="caption"
                    display="block"
                    color="textSecondary"
                    className={classes.subTitle}
                  >
                    즐겨찾기
                  </Typography>
                  <Grid container spacing={1}>
                    {favoriteMenu.map(favoriteMenuLink => {
                      const menuList = [].concat(
                        ...menus.map(data => data.list)
                      )
                      const subMenuData = menuList.find(
                        data => data?.link === favoriteMenuLink
                      )
                      if (!subMenuData) return null
                      return (
                        <Grid item xs={12}>
                          <MenuItem
                            classes={classes}
                            data={subMenuData}
                            isFavorite
                            onClickFavoriteIcon={onClickFavoriteIcon}
                          />
                        </Grid>
                      )
                    })}
                  </Grid>
                </Grid>
              </Box>
              <Box mt={1}>
                <Divider />
              </Box>
            </>
          )}
        <Grid container spacing={1} className={classes.mainGrid}>
          {menus
            .filter(menuData => {
              if (
                menuData.showAppLayout === false ||
                menuData?.hide === true ||
                menuData?.admin === true
              ) {
                return false
              }
              return true
            })
            .map(menuData => {
              return (
                <>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      display="block"
                      color="textSecondary"
                      className={classes.subTitle}
                    >
                      {menuData.headerTitle}
                    </Typography>
                    <Grid container spacing={1}>
                      {menuData.list
                        .filter(subMenuData => {
                          if (
                            subMenuData.admin === true ||
                            subMenuData.showAppLayout === false ||
                            subMenuData.hide === true
                          ) {
                            return false
                          }
                          return true
                        })
                        .map(subMenuData => {
                          const isFavorite = favoriteMenu.find(
                            favoriteMenuLink =>
                              favoriteMenuLink === subMenuData?.link
                          )
                          return (
                            <Grid item xs={12}>
                              <MenuItem
                                classes={classes}
                                data={subMenuData}
                                isFavorite={isFavorite}
                                onClickFavoriteIcon={onClickFavoriteIcon}
                              />
                            </Grid>
                          )
                        })}
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </>
              )
            })}
        </Grid>
      </Drawer>
    )
  },
  (prevProps, nextProps) => {
    return isEqualProps(nextProps, prevProps, [
      'open',
      'width',
      'menus',
      'favoriteMenu'
    ])
  }
)

MySiteLayoutDrawerMenu.defaultProps = setDefaultProps(useProps)
MySiteLayoutDrawerMenu.propTypes = setPropTypes(useProps)
export default withStyles(styles)(withWidth()(MySiteLayoutDrawerMenu))
